import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form, Spinner, Dropdown } from "react-bootstrap";
import { useApolloClient, useLazyQuery, useMutation } from "@apollo/client";
import { useToasts } from "react-toast-notifications";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faComment, faXmark, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { COLOR_APP } from "../../constants";
import { SHOP_APP_V2 } from "../../graphql/shop/queryShop";
import { GET_CONVERSATIONS, GET_CONVERSATION_THREADS } from "../../graphql/conversation/query";
import { REPLY_TO_CONVERSATION, MARK_CONVERSATION_READ } from "../../graphql/conversation/mutation";
import { onInboxEvent, requestUnreadRefresh } from "../../helpers/inboxEvents";
import { onSelectedPageChange, readSelectedPage, saveSelectedPage } from "../../helpers/selectedPage";
import { uploadChatMedia } from "../../helpers/uploadChatMedia";
import ChatMediaButtons from "./ChatMediaButtons";
import ResendBillButton from "../../components/ResendBillButton";
import ShowImageFull from "../../components/ShowImageFull";
import useWindowDimensions from "../../helpers/useWindowDimensions";
import { isInboxSoundOn } from "../../helpers/inboxSound";

//* Below this the list and the chat take turns on screen, as Messenger does on a phone.
const PHONE_MAX = 767;
//* iPad portrait and landscape: both panes, the list narrower.
const TABLET_MAX = 1199;

//* Text the server stores for a media-only message; the bubble shows the media instead.
const MEDIA_LABELS = ["📷 ຮູບພາບ", "🎤 ຂໍ້ຄວາມສຽງ", "🎬 ວິດີໂອ", "📎 ໄຟລ໌"];

const Attachment = ({ a, onOpenImage }) => {
    if (a.type === "image") {
        //* Opens full size over the chat (ShowImageFull), not in a new tab.
        return (
            <img
                src={a.url}
                alt=""
                onClick={() => onOpenImage(a.url)}
                style={{ maxWidth: "min(260px, 60vw)", maxHeight: 260, borderRadius: 10, display: "block", cursor: "zoom-in" }}
            />
        );
    }
    if (a.type === "audio") return <audio controls src={a.url} style={{ maxWidth: "min(260px, 62vw)" }} />;
    if (a.type === "video") return <video controls src={a.url} style={{ maxWidth: "min(260px, 62vw)", borderRadius: 10 }} />;
    return <a href={a.url} target="_blank" rel="noopener noreferrer">📎 ໄຟລ໌</a>;
};

// Short Lao relative time, e.g. "ດຽວນີ້", "5 ນາທີ", "3 ຊມ.", "2 ມື້" — falls back to a date once it's old.
const formatRelativeTime = (dateStr) => {
    if (!dateStr) return "";
    const diffMs = Date.now() - new Date(dateStr).getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "ດຽວນີ້";
    if (diffMin < 60) return `${diffMin} ນາທີ`;
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return `${diffHour} ຊມ.`;
    const diffDay = Math.floor(diffHour / 24);
    if (diffDay < 7) return `${diffDay} ມື້`;
    return new Date(dateStr).toLocaleDateString("lo-LA", { day: "numeric", month: "short" });
};

//* Added by the shop in public/void/. Played when a customer writes, on any connected page.
const NOTIFY_SOUND = `${process.env.PUBLIC_URL || ""}/void/notification.mp3`;
/**
 * The safety net under the subscription. The socket delivers in well under a second, but it
 * cannot deliver what is never published — the automatic bill and the address card are written
 * without an event — or what arrives while it is reconnecting. A quiet refetch closes both, so
 * nobody has to press refresh. Paused while the tab is hidden.
 */
const POLL_MS = 15000;

function MessengerInbox() {
    const { addToast } = useToasts();
    const client = useApolloClient();
    const shopId = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("SHOP_DATA"))?.id;
        } catch (e) {
            return undefined;
        }
    }, []);

    const [pages, setPages] = useState([]); // [{_id,name,token,status}]
    const [selectedPageId, setSelectedPageId] = useState(null);
    const [threads, setThreads] = useState([]); // [{psid,name,lastMessage,lastDirection,lastCreatedAt,unreadCount}]
    const [messages, setMessages] = useState([]); // full history of the selected thread
    const [selectedPsid, setSelectedPsid] = useState(null);
    const [draftMessage, setDraftMessage] = useState("");
    const messagesEndRef = useRef(null);

    const [getShop, { loading: loadingShop }] = useLazyQuery(SHOP_APP_V2, {
        fetchPolicy: "network-only",
        onCompleted: (data) => {
            const activePages = (data?.shop?.pages || []).filter((p) => p?.status);
            setPages(activePages);
            if (!selectedPageId && activePages.length > 0) {
                //* The page chosen last time, while it is still connected; otherwise the first.
                const saved = readSelectedPage();
                const keep = activePages.find((p) => p._id === saved);
                setSelectedPageId(keep ? keep._id : activePages[0]._id);
            }
        },
    });

    useEffect(() => {
        if (shopId) getShop({ variables: { where: { id: shopId } } });
    }, [shopId, getShop]);

    /**
     * Switching page loads that page AT ONCE, and shows nothing of the old one meanwhile.
     *
     * This was a useLazyQuery whose onCompleted set the list. On @apollo/client 3.7.1 that
     * callback does not reliably fire when the same lazy query is re-run with new variables, so a
     * switch could leave the previous page's threads on screen until something else refreshed
     * them. It now goes through refreshThreads (client.query), which also drops a response that
     * arrives after the shop has already moved on to another page.
     */
    const [loadingThreads, setLoadingThreads] = useState(false);
    useEffect(() => {
        setSelectedPsid(null);
        setMessages([]);
        setThreads([]);
        if (!selectedPageId) return;
        setLoadingThreads(true);
        const pageId = selectedPageId;
        refreshThreads(pageId).finally(() => {
            if (live.current.selectedPageId === pageId) setLoadingThreads(false);
        });
    }, [selectedPageId]);

    const [getConversations, { loading: loadingConversations }] = useLazyQuery(GET_CONVERSATIONS, {
        fetchPolicy: "network-only",
        onCompleted: (data) => {
            setMessages(data?.conversations || []);
        },
    });

    const [markConversationRead] = useMutation(MARK_CONVERSATION_READ);
    const [replyToConversation, { loading: sending }] = useMutation(REPLY_TO_CONVERSATION);

    useEffect(() => {
        if (selectedPageId && selectedPsid) {
            getConversations({ variables: { where: { pageId: selectedPageId, psid: selectedPsid } } });
            markConversationRead({ variables: { psid: selectedPsid, pageId: selectedPageId } }).then(requestUnreadRefresh).catch(() => {});
            setThreads((prev) => prev.map((t) => (t.psid === selectedPsid ? { ...t, unreadCount: 0 } : t)));
        }
    }, [selectedPsid]);

    // ── realtime ─────────────────────────────────────────────────────────────────────────
    //* New customer messages on pages other than the one on screen, for the page picker.
    const [pageNewCount, setPageNewCount] = useState({});
    //* The selection as it is NOW, for socket and timer callbacks that outlive a render.
    const live = useRef({});
    live.current = { selectedPageId, selectedPsid };
    //* Newest inbound time already announced, per page — so the socket and the safety-net
    //* refetch never ring twice for one message.
    const lastInboundAt = useRef({});
    const audioRef = useRef(null);
    const lastSoundAt = useRef(0);

    const playSound = useCallback(() => {
        //* Read on every ring, so switching it in ຕັ້ງຄ່າ takes effect without a reload.
        if (!isInboxSoundOn()) return;
        //* One ring for a burst: ten messages in a second is one notification, not ten.
        if (Date.now() - lastSoundAt.current < 1500) return;
        lastSoundAt.current = Date.now();
        try {
            if (!audioRef.current) audioRef.current = new Audio(NOTIFY_SOUND);
            audioRef.current.currentTime = 0;
            //* Browsers refuse sound until the user has clicked the page once; that is not an error.
            const p = audioRef.current.play();
            if (p && p.catch) p.catch(() => {});
        } catch (e) { /* no audio support */ }
    }, []);

    //* True when this is newer than anything already announced for the page.
    const isNewInbound = (pageId, createdAt) => {
        const t = new Date(createdAt || 0).getTime();
        if (!t || t <= (lastInboundAt.current[pageId] || 0)) return false;
        lastInboundAt.current[pageId] = t;
        return true;
    };

    //* Silent refetches: no spinner, and the open thread is only replaced when it changed, so a
    //* shop reading older messages is not scrolled back to the bottom every 15 seconds.
    const refreshThreads = useCallback(async (pageId) => {
        try {
            const { data } = await client.query({
                query: GET_CONVERSATION_THREADS, variables: { pageId }, fetchPolicy: "network-only",
            });
            const list = data?.conversationThreads || [];
            const newest = list
                .filter((t) => t.lastDirection !== "outbound")
                .reduce((m, t) => Math.max(m, new Date(t.lastCreatedAt || 0).getTime()), 0);
            //* A message the socket missed still rings, but not on the first load of a page.
            const known = lastInboundAt.current[pageId];
            if (known !== undefined && newest > known) playSound();
            if (newest) lastInboundAt.current[pageId] = Math.max(known || 0, newest);
            if (live.current.selectedPageId === pageId) setThreads(list);
        } catch (e) { /* the next tick retries */ }
    }, [client, playSound]);

    const refreshMessages = useCallback(async (pageId, psid) => {
        try {
            const { data } = await client.query({
                query: GET_CONVERSATIONS, variables: { where: { pageId, psid } }, fetchPolicy: "network-only",
            });
            const list = data?.conversations || [];
            const { selectedPageId: p, selectedPsid: s } = live.current;
            if (p !== pageId || s !== psid) return;
            setMessages((prev) => {
                const same = prev.length === list.length
                    && (prev[prev.length - 1]?.id || null) === (list[list.length - 1]?.id || null);
                return same ? prev : list;
            });
        } catch (e) { /* the next tick retries */ }
    }, [client]);

    const onIncoming = useRef(() => {});
    onIncoming.current = (incoming, pageId) => {
        if (!incoming) return;
        const inbound = incoming.direction !== "outbound";
        //* InboxNotifier has already rung for it; recorded here only so the safety-net refetch
        //* below does not ring a second time for the same message.
        if (inbound) isNewInbound(pageId, incoming.createdAt);
        const { selectedPageId: openPage, selectedPsid: openPsid } = live.current;
        if (pageId === openPage) {
            refreshThreads(pageId);
            if (incoming.psid === openPsid) {
                //* Deduped only on a real id. An event can arrive with id null (the reply path
                //* published the raw document), and treating null as an id dropped every reply
                //* after the first one.
                setMessages((prev) =>
                    incoming.id && prev.some((m) => m.id === incoming.id) ? prev : [...prev, incoming]);
                if (inbound) markConversationRead({ variables: { psid: openPsid, pageId } }).then(requestUnreadRefresh).catch(() => {});
            }
        } else if (inbound) {
            setPageNewCount((prev) => ({ ...prev, [pageId]: (prev[pageId] || 0) + 1 }));
        }
    };

    /**
     * Messages come from InboxNotifier, which holds the app's one subscription per connected page
     * on every screen and rings for them. Listening here rather than subscribing again keeps it to
     * one socket subscription per page, and one ring per message.
     */
    useEffect(() => onInboxEvent((incoming, pageId) => onIncoming.current(incoming, pageId)), []);

    //* The safety net: every POLL_MS while visible, and at once when the tab comes back.
    useEffect(() => {
        if (!selectedPageId) return undefined;
        const tick = () => {
            if (document.hidden) return;
            refreshThreads(selectedPageId);
            const psid = live.current.selectedPsid;
            if (psid) refreshMessages(selectedPageId, psid);
        };
        const timer = setInterval(tick, POLL_MS);
        const onBack = () => { if (!document.hidden) tick(); };
        document.addEventListener("visibilitychange", onBack);
        window.addEventListener("focus", onBack);
        return () => {
            clearInterval(timer);
            document.removeEventListener("visibilitychange", onBack);
            window.removeEventListener("focus", onBack);
        };
    }, [selectedPageId, refreshThreads, refreshMessages]);

    const selectPage = (pageId) => {
        setSelectedPageId(pageId);
        setPageNewCount((prev) => ({ ...prev, [pageId]: 0 }));
        saveSelectedPage(pageId);
    };
    //* Picked on the all-posts screen or in another tab: follow it, if this shop has that page.
    useEffect(() => onSelectedPageChange((id) => {
        if (id !== selectedPageId && pages.some((p) => p._id === id)) {
            setSelectedPageId(id);
            setPageNewCount((prev) => ({ ...prev, [id]: 0 }));
        }
    }), [pages, selectedPageId]);
    const otherPagesNew = Object.entries(pageNewCount)
        .filter(([id]) => id !== selectedPageId)
        .reduce((n, [, c]) => n + c, 0);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ block: "end" });
        }
    }, [messages]);

    const selectedThread = threads.find((t) => t.psid === selectedPsid);
    const selectedPage = pages.find((p) => p._id === selectedPageId);

    //* An image or voice clip waiting above the text box: { type, blob, ext, contentType, previewUrl }.
    const [pendingMedia, setPendingMedia] = useState(null);
    //* The chat image being shown full size, or null.
    const [fullImage, setFullImage] = useState(null);
    const { width: screenW, height: screenH } = useWindowDimensions();
    const isPhone = (screenW || 1280) <= PHONE_MAX;
    const isTablet = !isPhone && (screenW || 1280) <= TABLET_MAX;
    //* On a phone only one pane shows: the list, or the chat that was opened from it.
    const showList = !isPhone || !selectedPsid;
    const showChat = !isPhone || !!selectedPsid;
    const closeFullImage = useCallback(() => setFullImage(null), []);
    const [uploading, setUploading] = useState(false);
    const busy = sending || uploading;
    const clearPending = () => {
        if (pendingMedia?.previewUrl) URL.revokeObjectURL(pendingMedia.previewUrl);
        setPendingMedia(null);
    };
    //* A clip recorded for one customer is not sent to the next one opened.
    useEffect(() => { clearPending(); }, [selectedPsid]);

    const handleSend = async () => {
        const text = draftMessage.trim();
        if ((!text && !pendingMedia) || !selectedPsid || !selectedPageId || busy) return;
        try {
            let attachment = null;
            if (pendingMedia) {
                setUploading(true);
                const url = await uploadChatMedia(client, pendingMedia.blob, pendingMedia.ext, pendingMedia.contentType);
                attachment = { type: pendingMedia.type, url };
            }
            await replyToConversation({
                variables: {
                    psid: selectedPsid,
                    pageId: selectedPageId,
                    message: text || null,
                    attachment,
                },
            });
            setDraftMessage("");
            clearPending();
            refreshThreads(selectedPageId);
        } catch (error) {
            console.log("replyToConversation error:", error);
            const textWent = /TEXT_SENT_BUT_MEDIA_FAILED/.test(String(error?.message));
            if (textWent) setDraftMessage("");
            addToast(textWent ? "ສົ່ງຂໍ້ຄວາມແລ້ວ ແຕ່ສົ່ງຮູບ/ສຽງບໍ່ສຳເລັດ, ກະລຸນາລອງໃໝ່" : "ສົ່ງຂໍ້ຄວາມບໍ່ສຳເລັດ, ກະລຸນາລອງໃໝ່",
                { appearance: "error", autoDismiss: true });
        } finally {
            setUploading(false);
        }
    };

    return (
        //* innerHeight, not 100vh: on a phone 100vh includes the address bar and hid the text box.
        <div style={{ display: "flex", flexDirection: "column", height: screenH ? `${screenH - 80}px` : "calc(100vh - 80px)", padding: isPhone ? "6px 0" : "10px 0" }}>
            <div style={{ display: isPhone && selectedPsid ? "none" : "flex", alignItems: "center", flexWrap: "wrap", gap: isPhone ? 8 : 12, marginBottom: isPhone ? 8 : 14 }}>
                <FontAwesomeIcon icon={faComment} style={{ color: COLOR_APP, fontSize: 20 }} />
                <span style={{ fontWeight: "bold", fontSize: isPhone ? 18 : 22 }}>ຂໍ້ຄວາມລູກຄ້າ</span>

                {pages.length > 1 && (
                    <Dropdown onSelect={(key) => selectPage(key)}>
                        <Dropdown.Toggle size="sm" variant="outline-secondary" style={{ fontSize: 15 }}>
                            {selectedPage?.name || "ເລືອກ Page"}
                            {otherPagesNew > 0 && (
                                <span style={{ marginLeft: 8, background: "#E53935", color: "#fff", borderRadius: 10, padding: "0 7px", fontSize: 12 }}>
                                    {otherPagesNew}
                                </span>
                            )}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {pages.map((p) => (
                                <Dropdown.Item key={p._id} eventKey={p._id} style={{ fontSize: 15 }}>
                                    {p.name}
                                    {pageNewCount[p._id] > 0 && p._id !== selectedPageId && (
                                        <span style={{ marginLeft: 8, background: "#E53935", color: "#fff", borderRadius: 10, padding: "0 7px", fontSize: 12 }}>
                                            {pageNewCount[p._id]}
                                        </span>
                                    )}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                )}
            </div>

            {loadingShop ? (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 40 }}>
                    <Spinner animation="border" />
                </div>
            ) : pages.length === 0 ? (
                <div style={{ textAlign: "center", color: "#888", marginTop: 40, fontSize: 16 }}>
                    ຍັງບໍ່ໄດ້ເຊື່ອມ Facebook Page — ໄປທີ່ໜ້າຕັ້ງຄ່າເພື່ອເຊື່ອມ Page ກ່ອນ
                </div>
            ) : (
                <div
                    style={{
                        flex: 1,
                        display: "flex",
                        background: "#FFF",
                        borderRadius: 12,
                        boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)",
                        overflow: "hidden",
                    }}
                >
                    {/* Thread list */}
                    <div style={{
                        display: showList ? "block" : "none",
                        width: isPhone ? "100%" : isTablet ? 280 : 360,
                        flexShrink: 0, borderRight: isPhone ? "none" : "1px solid #F5F5FA", overflow: "auto",
                        padding: isPhone ? 6 : 10,
                    }}>
                        <div style={{ color: COLOR_APP, fontSize: 14, fontWeight: "bold", padding: "6px 8px" }}>
                            {loadingThreads ? <Spinner size="sm" animation="border" /> : `${threads.length} ລາຍການ`}
                        </div>
                        {threads.map((thread) => (
                            <div
                                key={thread.psid}
                                onClick={() => setSelectedPsid(thread.psid)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 12,
                                    padding: 12,
                                    borderRadius: 10,
                                    marginBottom: 6,
                                    cursor: "pointer",
                                    background: selectedPsid === thread.psid ? "#EEF0FF" : "transparent",
                                }}
                            >
                                {thread.profilePic ? (
                                    <img
                                        src={thread.profilePic}
                                        alt=""
                                        style={{ width: isTablet ? 40 : 48, height: isTablet ? 40 : 48, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                                    />
                                ) : (
                                    <div
                                        style={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: "50%",
                                            background: "#E8E8F0",
                                            flexShrink: 0,
                                        }}
                                    />
                                )}
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                                        <span style={{ fontSize: 16, fontWeight: "bold", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {thread.name || thread.psid}
                                        </span>
                                        <span style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                                            <span style={{ fontSize: 12, color: "#aaa", whiteSpace: "nowrap" }}>
                                                {formatRelativeTime(thread.lastCreatedAt)}
                                            </span>
                                            {thread.unreadCount > 0 && (
                                                <span style={{ background: "#FF3B30", color: "#FFF", borderRadius: 10, fontSize: 12, padding: "1px 8px" }}>
                                                    {thread.unreadCount}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: 14, color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {thread.lastMessage}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Thread view */}
                    <div style={{ flex: 1, display: showChat ? "flex" : "none", flexDirection: "column", minWidth: 0 }}>
                        <div style={{ padding: isPhone ? "10px 12px" : "14px 18px", borderBottom: "1px solid #F5F5FA", display: "flex", alignItems: "center", gap: isPhone ? 10 : 12 }}>
                            {isPhone && (
                                <FontAwesomeIcon
                                    icon={faArrowLeft}
                                    title="ກັບຄືນ"
                                    onClick={() => setSelectedPsid(null)}
                                    style={{ fontSize: 20, cursor: "pointer", padding: "4px 6px", color: "#555" }}
                                />
                            )}
                            {selectedThread?.profilePic && (
                                <img
                                    src={selectedThread.profilePic}
                                    alt=""
                                    style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }}
                                />
                            )}
                            <span style={{ fontSize: isPhone ? 16 : 18, fontWeight: "bold", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {selectedThread?.name || selectedThread?.psid || "ແຊັດ"}
                            </span>
                        </div>
                        <div style={{ flex: 1, overflow: "auto", padding: isPhone ? 10 : 18 }}>
                            {loadingConversations ? (
                                <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
                                    <Spinner size="sm" animation="border" />
                                </div>
                            ) : (
                                messages.map((msg) => (
                                    <div
                                        key={msg.id}
                                        style={{
                                            display: "flex",
                                            justifyContent: msg.direction === "outbound" ? "flex-end" : "flex-start",
                                            margin: "8px 0",
                                        }}
                                    >
                                        <div
                                            style={{
                                                maxWidth: isPhone ? "85%" : isTablet ? "75%" : "60%",
                                                padding: isPhone ? "8px 12px" : "10px 16px",
                                                wordBreak: "break-word",
                                                whiteSpace: "pre-wrap",
                                                borderRadius: 14,
                                                background: msg.direction === "outbound" ? COLOR_APP : "#f2f2f2",
                                                color: msg.direction === "outbound" ? "#FFF" : "#000",
                                                fontSize: isPhone ? 15 : 16,
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            {(msg.attachments || []).map((a, i) => (
                                                <div key={i} style={{ marginBottom: msg.message ? 6 : 0 }}>
                                                    <Attachment a={a} onOpenImage={setFullImage} />
                                                </div>
                                            ))}
                                            {!(msg.attachments?.length && MEDIA_LABELS.includes(msg.message)) && msg.message}
                                            {msg.billDelivery?.status === "FAILED" && (
                                                <div style={{ marginTop: 6 }}>
                                                    <ResendBillButton
                                                        orderGroup={msg.billDelivery.orderGroup}
                                                        onSent={() => setMessages((prev) => prev.map((m) => (
                                                            m.id === msg.id
                                                                ? { ...m, billDelivery: { ...m.billDelivery, status: "SENT" } }
                                                                : m
                                                        )))}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                            {!selectedPsid && (
                                <div style={{ textAlign: "center", color: "#888", marginTop: 20, fontSize: 16 }}>ຍັງບໍ່ມີຂໍ້ຄວາມ</div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                        {pendingMedia && (
                            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px 0" }}>
                                {pendingMedia.type === "image" ? (
                                    <img
                                        src={pendingMedia.previewUrl}
                                        alt=""
                                        onClick={() => setFullImage(pendingMedia.previewUrl)}
                                        style={{ height: 72, borderRadius: 8, cursor: "zoom-in" }}
                                    />
                                ) : (
                                    <audio controls src={pendingMedia.previewUrl} style={{ height: 36 }} />
                                )}
                                <span style={{ fontSize: 13, color: "#888" }}>ກົດສົ່ງເພື່ອສົ່ງໃຫ້ລູກຄ້າ</span>
                                <FontAwesomeIcon
                                    icon={faXmark}
                                    title="ຍົກເລີກ"
                                    style={{ cursor: busy ? "not-allowed" : "pointer", color: "#999", fontSize: 18 }}
                                    onClick={() => !busy && clearPending()}
                                />
                            </div>
                        )}
                        <div style={{ display: "flex", gap: isPhone ? 6 : 10, padding: isPhone ? 8 : 14, borderTop: pendingMedia ? "none" : "1px solid #F5F5FA" }}>
                            <ChatMediaButtons
                                disabled={!selectedPsid || busy}
                                onPick={(m) => { clearPending(); setPendingMedia(m); }}
                                onError={(text) => addToast(text, { appearance: "warning", autoDismiss: true })}
                            />
                            <Form.Control
                                as="textarea"
                                rows={1}
                                placeholder="ພິມຂໍ້ຄວາມຕອບກັບ..."
                                value={draftMessage}
                                disabled={!selectedPsid}
                                style={{ fontSize: 16 }}
                                onChange={(e) => setDraftMessage(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSend();
                                    }
                                }}
                            />
                            <div
                                onClick={handleSend}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: isPhone ? 42 : 48,
                                    flexShrink: 0,
                                    borderRadius: 8,
                                    background: COLOR_APP,
                                    color: "#FFF",
                                    fontSize: 18,
                                    cursor: !selectedPsid || (!draftMessage.trim() && !pendingMedia) || busy ? "not-allowed" : "pointer",
                                    opacity: !selectedPsid || (!draftMessage.trim() && !pendingMedia) || busy ? 0.5 : 1,
                                }}
                            >
                                {busy ? <Spinner size="sm" animation="border" /> : <FontAwesomeIcon icon={faPaperPlane} />}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <ShowImageFull src={fullImage} onClose={closeFullImage} />
        </div>
    );
}

export default MessengerInbox;
